import React, { useContext, useState, useEffect, useRef } from "react";
import { Row, Col, Container } from "react-bootstrap";
import PackageCardNew from "./PackageCardNew";
import { OrgIdContext } from "../../store/orgId_context";
import { NotificationManager } from "react-notifications";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { DDD } from "../../lib/ddd/ddd";
import CloseIcon from '@mui/icons-material/Close';

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";

const PackageSelectorNew = ({ packages, goToOverview }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [orderPackage, setOrderPackage] = useState(null);
  const [missingFunds, setMissingFunds] = useState(false);
  const [contactUsOpen, setContactUsOpen] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutInstance, setCheckoutInstance] = useState(null);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const checkoutInstanceRef = useRef(null);
  const {
    sessionInfo,
    setSessionInfoHandler,
  } = useContext(OrgIdContext);

  useEffect(() => {
    const ddd = new DDD(apiRequestAddress);
    ddd.Rpc_ExecuteMethod("EUeInvoices.GetStripeSubscriptions", { Status: "Active" })
      .then((res) => {
        const subs = res?.result;
        setActiveSubscription(Array.isArray(subs) && subs.length > 0 ? subs[0] : null);
      });
    ddd.Rpc_ExecuteExtension("EUeInvoices.GetStripeConfig", {})
      .then((res) => {
        if (res?.result?.PublishableKey) setStripePublishableKey(res.result.PublishableKey);
      });
  }, []);

  const destroyCheckout = () => {
    if (checkoutInstanceRef.current) {
      try { checkoutInstanceRef.current.destroy(); } catch {}
      checkoutInstanceRef.current = null;
    }
    setCheckoutInstance(null);
  };

  useEffect(() => {
    return () => {
      if (checkoutInstanceRef.current) {
        try { checkoutInstanceRef.current.destroy(); } catch {}
      }
    };
  }, []);

  const handleSelect = (id) => {
    const pkg = packages.find((p) => p.Id === id);
    if (!pkg)
      return NotificationManager.error("Unknown error. Please contact administrator.", "", 5000);
    if (!pkg?.StripePriceId || pkg?.PackageCode?.toUpperCase() === "CUSTOM") return setContactUsOpen(true);
    if (!hasFunds()) return setMissingFunds(true);
    setOrderPackage(pkg);
    setConfirmDialog(true);
  };

  const hasFunds = () => {
    if (!sessionInfo?.Extended?.AccountBalance) return false;
    const availableFunds = parseFloat(sessionInfo.Extended.AccountBalance);
    let cost = parseFloat(orderPackage?.PricePerMonth);
    if (isNaN(cost)) cost = 0;
    return availableFunds >= cost;
  };

  const onConfirmPackageChange = () => {
    setConfirmDialog(false);
    if (!hasFunds()) return setMissingFunds(true);
    if (!orderPackage) return NotificationManager.error("No package selected.", "", 5000);

    const args = {
      PackageType: orderPackage?.PackageType,
      PaymentType: "Invoice",
      PayType: "Monthly",
      PackageId: orderPackage.Id,
    };
    const ddd = new DDD(apiRequestAddress);
    ddd.Rpc_ExecuteExtension("EUeInvoices.ChangePackageNew", args).then((res) => {
      if (res?.result?.Status === "Error") {
        NotificationManager.error(res.result.Reason, "Error", 7000);
      } else {
        ddd.InvalidateSessionExtensions().then(() => {
          ddd.GetSessionAuthDescribe({ includeExtendedInfo: true }).then((res) => {
            if (res?.result) setSessionInfoHandler(res.result);
            NotificationManager.success("Package changed successfully.", "Success", 5000);
          });
        });
      }
    });
  };

  const toOverview = () => {
    setMissingFunds(false);
    if (goToOverview) goToOverview();
  };

  const confirmCancel = () => setCancelConfirmOpen(true);

  const cancelSubscription = async () => {
    setCancelConfirmOpen(false);
    if (!activeSubscription) return;
    setCancelling(true);
    const ddd = new DDD(apiRequestAddress);

    try {
      const res = await ddd.Rpc_ExecuteMethod("EUeInvoices.CancelStripeSubscription", {
        SubscriptionId: activeSubscription.Id,
      });
      if (res?.result?.Status === "Error") {
        NotificationManager.error(res.result.Reason, "Error", 7000);
        setCancelling(false);
        return;
      }
    } catch {
      NotificationManager.error("Failed to cancel subscription.", "Error", 7000);
      setCancelling(false);
      return;
    }

    const cancelledCode = sessionInfo?.Extended?.CurrentPackages?.IEI_Package?.PackageCode;
    const maxAttempts = 5;

    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      await ddd.InvalidateSessionExtensions();
      try {
        const descRes = await ddd.GetSessionAuthDescribe({ includeExtendedInfo: true });
        if (descRes?.result) {
          setSessionInfoHandler(descRes.result);
          const newCode = descRes.result?.Extended?.CurrentPackages?.IEI_Package?.PackageCode;
          if (newCode !== cancelledCode) break;
        }
      } catch {}
    }

    setActiveSubscription(null);
    setCancelling(false);
    NotificationManager.success("Subscription cancelled.", "Success", 5000);
  };

  const startCheckout = async (pkg) => {
    if (!pkg?.StripePriceId || pkg?.PackageCode?.toUpperCase() === "CUSTOM")
      return setContactUsOpen(true);
    if (!stripePublishableKey)
      return NotificationManager.error("Stripe configuration not loaded.", "Error", 5000);

    const ddd = new DDD(apiRequestAddress);
    const returnUrl = window.location.origin + "/change-payment-plan?session_id={CHECKOUT_SESSION_ID}";

    try {
      const checkoutArgs = {
        PriceId: pkg.StripePriceId,
        PlanName: pkg.PackageCode,
        ReturnUrl: returnUrl,
        CustomerEmail: sessionInfo?.Email || undefined,
        AllowPromotionCodes: true,
        CollectTaxId: true,
        BillingAddressRequired: true,
        AutomaticTax: true,
      };
      console.log("[CreateStripeSubscription] args:", JSON.stringify(checkoutArgs, null, 2));
      const res = await ddd.Rpc_ExecuteMethod("EUeInvoices.CreateStripeSubscription", checkoutArgs);

      if (res?.result?.Status === "Error")
        return NotificationManager.error(res.result.Reason, "Error", 7000);

      const stripe = window.Stripe(stripePublishableKey);
      const instance = await stripe.initEmbeddedCheckout({
        clientSecret: res.result.ClientSecret,
        onComplete: async () => {
          await ddd.InvalidateSessionExtensions();
          const descRes = await ddd.GetSessionAuthDescribe({ includeExtendedInfo: true });
          if (descRes?.result) setSessionInfoHandler(descRes.result);
          setCheckoutOpen(false);
          destroyCheckout();
          NotificationManager.success("Subscription active!", "Success", 5000);
        },
      });
      setCheckoutInstance(instance);
      checkoutInstanceRef.current = instance;
      setCheckoutOpen(true);
    } catch (err) {
      NotificationManager.error(err.message || "Failed to start checkout.", "Error", 7000);
    }
  };

  return (
    <>
      <Dialog
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 435 } }}
        maxWidth="xs"
        TransitionProps={{}}
        open={confirmDialog}
      >
        <DialogTitle>Confirm package change</DialogTitle>
        <DialogContent dividers>
          {"Are you certain you wish to order package"}: {orderPackage?.PackageCode}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={onConfirmPackageChange}>Ok</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 435 } }}
        maxWidth="xs"
        TransitionProps={{}}
        open={missingFunds}
      >
        <DialogTitle>Missing credits</DialogTitle>
        <DialogContent dividers>
          Not enough credit balance to order this package. Please visit the Overview tab to upload more credit.
        </DialogContent>
        <DialogActions>
          <Button onClick={toOverview}>Go to Overview</Button>
          <Button onClick={() => setMissingFunds(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactUsOpen}
        onClose={() => setContactUsOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '90%',
            maxWidth: '700px',
            minHeight: '70vh',
            padding: '10px',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Contact Us
          <IconButton onClick={() => setContactUsOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: '0 24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '60vh' }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            {/* "45min" event was renamed/removed on Calendly's side (404) — "30min-intro"
                is the current slug, confirmed live on dddinvoices.com's own "Book a
                meeting" button. */}
            <iframe src="https://calendly.com/ddd-invoices/30min-intro" style={{ width: "100%", height: "100%" }}></iframe>
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={() => setContactUsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel subscription</DialogTitle>
        <DialogContent dividers>
          <p style={{ margin: 0, fontSize: '0.95rem' }}>
            Are you sure you want to cancel your subscription? You will lose access at the end of the current billing period.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>Keep subscription</Button>
          <Button onClick={cancelSubscription} style={{ color: '#dc2626' }}>Yes, cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={checkoutOpen}
        onClose={() => { destroyCheckout(); setCheckoutOpen(false); }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '500px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            borderRadius: '12px',
            padding: 0,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '8px 12px 0 12px' }}>
          <IconButton onClick={() => { destroyCheckout(); setCheckoutOpen(false); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '8px 0 0 0', overflow: 'auto', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' } }}>
          <div
            id="checkout-container"
            ref={(el) => { if (el && checkoutInstance) checkoutInstance.mount(el); }}
          />
        </DialogContent>
      </Dialog>

      <Container fluid className="mt-4">
        <Row className="g-4">
          {packages?.map((pkg) => (
            <Col key={pkg.Id} xl={3} lg={4} md={6} sm={6} xs={12}>
              <PackageCardNew
                pkg={pkg}
                selectedId={selectedId}
                onSelect={handleSelect}
                onCancel={confirmCancel}
                cancelling={cancelling}
                onCheckout={startCheckout}
              />
            </Col>
          ))}
        </Row>
      </Container>
    </>
  );
};

export default PackageSelectorNew;
